import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import GlobalFooter from '../components/GlobalFooter';
import { toast } from 'sonner';
import { 
  ArrowLeft, BookOpen, Clock, User, 
  Search, Sparkles, ChevronRight, Calendar,
  Share2, Twitter, Facebook, Linkedin, Link2, X,
  Mail, CheckCircle2, Loader2
} from 'lucide-react';

// Static blog posts for now (can be made dynamic later)
const blogPosts = [
  {
    id: '1',
    title: 'Understanding Vitamin D: More Than Just the Sunshine Vitamin',
    excerpt: 'Vitamin D plays a crucial role beyond bone health. Learn about its impact on immunity, mood, and overall wellness.',
    author: 'Vitala Team',
    date: '2026-03-01',
    readTime: '5 min read',
    category: 'Education',
    tags: ['Vitamin D', 'Immunity', 'Wellness'],
    image: null
  },
  {
    id: '2',
    title: 'How to Read Supplement Labels: A Beginner\'s Guide',
    excerpt: 'Confused by supplement labels? Learn what to look for, what to avoid, and how to choose quality products.',
    author: 'Vitala Team',
    date: '2026-02-25',
    readTime: '7 min read',
    category: 'Guide',
    tags: ['Supplements', 'Quality', 'Shopping'],
    image: null
  },
  {
    id: '3',
    title: 'The Science Behind Magnesium: Types and Benefits',
    excerpt: 'Not all magnesium is created equal. Discover the different forms and which one might be right for you.',
    author: 'Vitala Team',
    date: '2026-02-18',
    readTime: '6 min read',
    category: 'Education',
    tags: ['Magnesium', 'Sleep', 'Stress'],
    image: null
  },
  {
    id: '4',
    title: 'Supplements and Medications: What You Need to Know',
    excerpt: 'Important considerations for taking supplements alongside prescription medications.',
    author: 'Vitala Team',
    date: '2026-02-10',
    readTime: '8 min read',
    category: 'Safety',
    tags: ['Interactions', 'Medications', 'Safety'],
    image: null
  },
  {
    id: '5',
    title: 'Building a Morning Supplement Routine',
    excerpt: 'Tips for creating an effective and sustainable morning supplement routine that works for your lifestyle.',
    author: 'Vitala Team',
    date: '2026-02-05',
    readTime: '4 min read',
    category: 'Lifestyle',
    tags: ['Routine', 'Morning', 'Tips'],
    image: null
  }
];

const categories = ['All', 'Education', 'Guide', 'Safety', 'Lifestyle', 'Research'];

export default function CommunityPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [shareMenuOpen, setShareMenuOpen] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShareMenuOpen(null);
    if (shareMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [shareMenuOpen]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setSubscribing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail, name: newsletterName || null }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubscribed(true);
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getCategoryColor = (category) => {
    const colors = {
      'Education': 'bg-blue-100 text-blue-700',
      'Guide': 'bg-green-100 text-green-700',
      'Safety': 'bg-red-100 text-red-700',
      'Lifestyle': 'bg-purple-100 text-purple-700',
      'Research': 'bg-amber-100 text-amber-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const getShareUrl = (post) => {
    return `https://vitala.me/blog/${post.id}`;
  };

  const shareToTwitter = (post) => {
    const url = getShareUrl(post);
    const text = `${post.title} - Check out this health article from Vitala!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'width=550,height=420');
    setShareMenuOpen(null);
  };

  const shareToFacebook = (post) => {
    const url = getShareUrl(post);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=550,height=420');
    setShareMenuOpen(null);
  };

  const shareToLinkedIn = (post) => {
    const url = getShareUrl(post);
    const title = post.title;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=550,height=420');
    setShareMenuOpen(null);
  };

  const copyLink = (post) => {
    const url = getShareUrl(post);
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
    setShareMenuOpen(null);
  };

  const ShareMenu = ({ post, position = 'bottom' }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} right-0 bg-white rounded-xl shadow-lg border border-border p-2 z-20 min-w-[180px]`}
    >
      <div className="text-xs font-medium text-muted-foreground px-3 py-1 mb-1">Share this article</div>
      <button
        onClick={(e) => { e.stopPropagation(); shareToTwitter(post); }}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
      >
        <div className="w-8 h-8 rounded-full bg-[#1DA1F2]/10 flex items-center justify-center">
          <Twitter className="w-4 h-4 text-[#1DA1F2]" />
        </div>
        <span>Twitter / X</span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); shareToFacebook(post); }}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
      >
        <div className="w-8 h-8 rounded-full bg-[#1877F2]/10 flex items-center justify-center">
          <Facebook className="w-4 h-4 text-[#1877F2]" />
        </div>
        <span>Facebook</span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); shareToLinkedIn(post); }}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
      >
        <div className="w-8 h-8 rounded-full bg-[#0A66C2]/10 flex items-center justify-center">
          <Linkedin className="w-4 h-4 text-[#0A66C2]" />
        </div>
        <span>LinkedIn</span>
      </button>
      <div className="border-t border-border my-1"></div>
      <button
        onClick={(e) => { e.stopPropagation(); copyLink(post); }}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
      >
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
          <Link2 className="w-4 h-4 text-gray-600" />
        </div>
        <span>Copy Link</span>
      </button>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-16 sm:w-20" />
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <BookOpen className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
            Health & Wellness Blog
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Expert insights, guides, and tips to help you on your wellness journey.
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          {/* Search */}
          <div className="relative max-w-md mx-auto mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-border rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-white border border-border hover:border-primary/30'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="bg-white rounded-2xl border border-border overflow-hidden hover:border-primary/30 transition-colors">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(filteredPosts[0].category)}`}>
                      {filteredPosts[0].category}
                    </span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {filteredPosts[0].readTime}
                    </span>
                  </div>
                  {/* Share Button */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareMenuOpen(shareMenuOpen === filteredPosts[0].id ? null : filteredPosts[0].id);
                      }}
                      className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                      data-testid="share-button-featured"
                    >
                      <Share2 className="w-5 h-5 text-muted-foreground" />
                    </button>
                    {shareMenuOpen === filteredPosts[0].id && <ShareMenu post={filteredPosts[0]} />}
                  </div>
                </div>
                <div onClick={() => toast.info('Full article coming soon!')} className="cursor-pointer">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">
                    {filteredPosts[0].title}
                  </h2>
                  <p className="text-muted-foreground mb-4 text-lg">
                    {filteredPosts[0].excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="w-4 h-4" />
                      <span>{filteredPosts[0].author}</span>
                      <span className="mx-2">•</span>
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(filteredPosts[0].date).toLocaleDateString()}</span>
                    </div>
                    <span className="text-primary font-medium flex items-center gap-1">
                      Read More <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Blog Posts Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.slice(1).map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (index + 3) }}
              className="bg-white rounded-xl border border-border overflow-hidden hover:border-primary/30 transition-colors"
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                    <span className="text-xs text-muted-foreground">{post.readTime}</span>
                  </div>
                  {/* Share Button */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareMenuOpen(shareMenuOpen === post.id ? null : post.id);
                      }}
                      className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                      data-testid={`share-button-${post.id}`}
                    >
                      <Share2 className="w-4 h-4 text-muted-foreground" />
                    </button>
                    {shareMenuOpen === post.id && <ShareMenu post={post} position="bottom" />}
                  </div>
                </div>
                <div onClick={() => toast.info('Full article coming soon!')} className="cursor-pointer">
                  <h3 className="font-semibold text-[#1A1A1A] mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {post.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No articles found matching your search.</p>
          </div>
        )}

        {/* Newsletter Signup */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-6 sm:p-8"
        >
          {subscribed ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">You're Subscribed!</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Check your inbox for a welcome email. We'll send you weekly health tips and insights.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A] mb-2">
                  Get Weekly Health Tips
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Join our newsletter for evidence-based supplement insights, wellness tips, and exclusive guides delivered to your inbox.
                </p>
              </div>
              
              <form onSubmit={handleNewsletterSubmit} className="max-w-lg mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Your name (optional)"
                      value={newsletterName}
                      onChange={(e) => setNewsletterName(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      className="bg-white"
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={subscribing}
                    className="btn-primary whitespace-nowrap"
                    data-testid="newsletter-subscribe-button"
                  >
                    {subscribing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Subscribe
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  No spam, unsubscribe anytime. We respect your privacy.
                </p>
              </form>
            </>
          )}
        </motion.section>
      </main>

      <GlobalFooter />
    </div>
  );
}
